import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toLoginEmail } from '@/lib/adminAuth';

const errorText = (message: string) => {
  const lower = message.toLowerCase();
  if (lower.includes('invalid login credentials')) return '账号或密码不对，再试一次';
  if (lower.includes('email not confirmed')) return '这个账号还没确认，请到 Supabase 后台确认一下';
  if (lower.includes('too many requests')) return '尝试次数太多了，等一分钟再来';
  return message;
};

export default function AdminLogin() {
  const navigate = useNavigate();
  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (active && data.session) navigate('/admin', { replace: true });
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [navigate]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!account.trim() || !password) {
      setError('账号和密码都要填');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: toLoginEmail(account),
        password,
      });

      if (signInError) {
        setError(errorText(signInError.message));
        setLoading(false);
        return;
      }

      navigate('/admin', { replace: true });
    } catch {
      setError('登录失败，网络可能不太稳，再试一次');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-100 flex flex-col relative">
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute -top-24 -left-24 w-[380px] h-[380px] rounded-full bg-primary-200/35 blur-3xl" />
        <div className="absolute bottom-0 -right-24 w-[420px] h-[420px] rounded-full bg-accent-200/30 blur-3xl" />
      </div>

      <div className="relative flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-sm">
          <div className="text-center mb-6">
            <span className="inline-flex w-14 h-14 rounded-2xl bg-primary-500 text-background-50 items-center justify-center mb-3">
              <i className="ri-heart-pulse-line text-2xl"></i>
            </span>
            <h1 className="text-xl font-heading font-semibold text-foreground-950">郡主后台</h1>
            <p className="text-xs text-foreground-500 mt-1.5">更年期健康自查 · 问诊记录管理</p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-background-50 rounded-2xl border border-secondary-200/80 shadow-sm p-5 space-y-4 mt-6 box-border"
          >
            <div className="min-w-0">
              <label htmlFor="admin-account" className="block text-xs font-medium text-foreground-600 mb-1.5">
                管理员账号
              </label>
              <div className="flex items-center gap-2 h-11 px-3 rounded-xl border border-secondary-200 bg-background-100 focus-within:border-primary-400 transition-colors box-border">
                <i className="ri-user-3-line text-foreground-400 text-sm"></i>
                <input
                  id="admin-account"
                  name="account"
                  type="text"
                  value={account}
                  onChange={(event) => setAccount(event.target.value)}
                  placeholder="输入账号"
                  autoComplete="username"
                  className="flex-1 min-w-0 bg-transparent border-0 outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
                />
              </div>
            </div>

            <div className="min-w-0">
              <label htmlFor="admin-password" className="block text-xs font-medium text-foreground-600 mb-1.5">
                密码
              </label>
              <div className="flex items-center gap-2 h-11 px-3 rounded-xl border border-secondary-200 bg-background-100 focus-within:border-primary-400 transition-colors box-border">
                <i className="ri-lock-2-line text-foreground-400 text-sm"></i>
                <input
                  id="admin-password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="输入密码"
                  autoComplete="current-password"
                  className="flex-1 min-w-0 bg-transparent border-0 outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  title={showPassword ? '隐藏密码' : '显示密码'}
                  className="w-6 h-6 flex items-center justify-center text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer shrink-0"
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </button>
              </div>
            </div>

            {error ? (
              <p className="text-xs text-accent-900 bg-accent-100 border border-accent-300 rounded-lg px-3 py-2 leading-relaxed flex gap-1.5">
                <i className="ri-error-warning-line shrink-0"></i>
                <span className="min-w-0 break-words">{error}</span>
              </p>
            ) : null}

            <button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-xl bg-primary-500 hover:bg-primary-600 disabled:opacity-70 disabled:cursor-not-allowed text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  正在登录
                </>
              ) : (
                <>
                  登录后台
                  <i className="ri-arrow-right-line"></i>
                </>
              )}
            </button>

            <p className="text-[11px] text-foreground-400 leading-relaxed text-center">
              账号由管理员统一分配，忘记密码请联系系统管理员重置。
            </p>
          </form>

          <div className="text-center mt-5">
            <Link
              to="/"
              className="text-xs text-foreground-500 hover:text-primary-600 transition-colors inline-flex items-center gap-1"
            >
              <i className="ri-arrow-left-line"></i>
              返回问诊表
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}